import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	ViewChild,
} from "@angular/core";

export type LayoutElementType = "IMAGE" | "VIDEO" | "BANNER";

export interface LayoutElement {
	id: string;
	name: string;
	type: LayoutElementType;
	x: number;
	y: number;
	width: number;
	height: number;

	fixedSize: boolean;

	/**
	 * Indica si el panel debe conservar su proporción visual.
	 */
	lockAspectRatio: boolean;

	/**
	 * Relación física ancho/alto guardada al activar el bloqueo.
	 */
	aspectRatio?: number;
}

type CollisionDirection = "n" | "e" | "s" | "w";

type ResizeHandle = "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";

type NumericField = "x" | "y" | "width" | "height";

interface InteractionState {
	mode: "move" | "resize";
	pointerId: number;
	elementId: string;
	handle?: ResizeHandle;

	startClientX: number;
	startClientY: number;

	canvasWidth: number;
	canvasHeight: number;

	originalElement: LayoutElement;
	originalElements: LayoutElement[];
}

@Component({
	selector: "app-layout-designer",
	templateUrl: "./layout-designer.component.html",
	styleUrls: ["./layout-designer.component.scss"],
})
export class LayoutDesignerComponent {
	private readonly canvasAspectRatio = 16 / 9;

	private isInsideCanvas(element: LayoutElement): boolean {
		const tolerance = 0.001;

		return (
			element.x >= -tolerance &&
			element.y >= -tolerance &&
			element.width >= this.minimumSize &&
			element.height >= this.minimumSize &&
			element.x + element.width <= 100 + tolerance &&
			element.y + element.height <= 100 + tolerance
		);
	}

	private hasSameGeometry(
		first: LayoutElement,
		second: LayoutElement,
	): boolean {
		const tolerance = 0.001;

		return (
			Math.abs(first.x - second.x) < tolerance &&
			Math.abs(first.y - second.y) < tolerance &&
			Math.abs(first.width - second.width) < tolerance &&
			Math.abs(first.height - second.height) < tolerance
		);
	}

	readonly layoutTypeOptions: Array<{
		value: LayoutElementType;
		label: string;
	}> = [
		{
			value: "IMAGE",
			label: "Imagen",
		},
		{
			value: "VIDEO",
			label: "Video",
		},
		{
			value: "BANNER",
			label: "Banner",
		},
	];

	@ViewChild("canvas", { static: true })
	private canvasRef!: ElementRef<HTMLElement>;

	@Input()
	set value(elements: Array<Partial<LayoutElement>> | null | undefined) {
		this.elements = (elements || []).map(
			(element, index): LayoutElement => {
				const normalizedElement: LayoutElement = {
					id: element.id || this.createId(),

					name: element.name || `Zona ${index + 1}`,

					type: element.type || "IMAGE",

					x: typeof element.x === "number" ? element.x : 0,

					y: typeof element.y === "number" ? element.y : 0,

					width:
						typeof element.width === "number" ? element.width : 25,

					height:
						typeof element.height === "number"
							? element.height
							: 25,

					fixedSize: element.fixedSize === true,

					lockAspectRatio: element.lockAspectRatio === true,

					aspectRatio:
						typeof element.aspectRatio === "number" &&
						element.aspectRatio > 0
							? element.aspectRatio
							: undefined,
				};

				if (
					normalizedElement.lockAspectRatio &&
					!normalizedElement.aspectRatio
				) {
					normalizedElement.aspectRatio =
						this.calculateVisualAspectRatio(normalizedElement);
				}

				return normalizedElement;
			},
		);

		this.selectedId = null;
	}

	updateAspectRatioLock(checked: boolean): void {
		const selected = this.selectedElement;

		if (!selected) {
			return;
		}

		const updatedElement: LayoutElement = {
			...selected,
			lockAspectRatio: checked,
			aspectRatio: checked
				? this.calculateVisualAspectRatio(selected)
				: undefined,
		};

		this.replaceElement(selected.id, updatedElement);

		this.errorMessage = "";
		this.emitValue();
	}

	private calculateVisualAspectRatio(element: LayoutElement): number {
		const safeHeight = Math.max(element.height, this.minimumSize);

		return (element.width * this.canvasAspectRatio) / safeHeight;
	}

	@Output()
	readonly valueChange = new EventEmitter<LayoutElement[]>();

	layoutName = "";

	elements: LayoutElement[] = [];
	selectedId: string | null = null;
	errorMessage = "";

	readonly resizeHandles: ResizeHandle[] = [
		"n",
		"ne",
		"e",
		"se",
		"s",
		"sw",
		"w",
		"nw",
	];

	private readonly minimumSize = 5;
	private readonly defaultWidth = 20;
	private readonly defaultHeight = 20;

	private interaction: InteractionState | null = null;

	get selectedElement(): LayoutElement | null {
		if (!this.selectedId) {
			return null;
		}

		return (
			this.elements.find((element) => element.id === this.selectedId) ||
			null
		);
	}

	addElement(): void {
		const position = this.findAvailablePosition(
			this.defaultWidth,
			this.defaultHeight,
		);

		if (!position) {
			this.errorMessage =
				"No existe suficiente espacio disponible para agregar otra zona.";

			return;
		}

		const element: LayoutElement = {
			id: this.createId(),
			name: `Zona ${this.elements.length + 1}`,
			type: "IMAGE",
			x: position.x,
			y: position.y,
			width: this.defaultWidth,
			height: this.defaultHeight,
			fixedSize: false,
			lockAspectRatio: false,
		};

		this.elements = [...this.elements, element];
		this.selectedId = element.id;
		this.errorMessage = "";

		this.emitValue();
	}

	updateType(value: string): void {
		const selected = this.selectedElement;

		if (!selected) {
			return;
		}

		if (!this.isLayoutElementType(value)) {
			this.errorMessage = "El tipo de espacio seleccionado no es válido.";

			return;
		}

		this.replaceElement(selected.id, {
			...selected,
			type: value,
		});

		this.errorMessage = "";
		this.emitValue();
	}

	private isLayoutElementType(value: string): value is LayoutElementType {
		return value === "IMAGE" || value === "VIDEO" || value === "BANNER";
	}

	duplicateSelected(): void {
		const selected = this.selectedElement;

		if (!selected) {
			return;
		}

		const position = this.findAvailablePosition(
			selected.width,
			selected.height,
		);

		if (!position) {
			this.errorMessage =
				"No existe un espacio libre para duplicar la zona seleccionada.";

			return;
		}

		const duplicated: LayoutElement = {
			...selected,
			id: this.createId(),
			name: `${selected.name} copia`,
			x: position.x,
			y: position.y,
		};

		this.elements = [...this.elements, duplicated];
		this.selectedId = duplicated.id;
		this.errorMessage = "";

		this.emitValue();
	}

	removeSelected(): void {
		if (!this.selectedId) {
			return;
		}

		this.elements = this.elements.filter(
			(element) => element.id !== this.selectedId,
		);

		this.selectedId = null;
		this.errorMessage = "";

		this.emitValue();
	}

	selectElement(elementId: string): void {
		this.selectedId = elementId;
		this.errorMessage = "";
	}

	getTypeLabel(type: LayoutElementType): string {
		switch (type) {
			case "IMAGE":
				return "Imagen";

			case "VIDEO":
				return "Video";

			case "BANNER":
				return "Banner";

			default:
				return type;
		}
	}

	onCanvasPointerDown(event: PointerEvent): void {
		if (event.target === event.currentTarget) {
			this.selectedId = null;
		}
	}

	startMove(event: PointerEvent, element: LayoutElement): void {
		if (event.button !== 0) {
			return;
		}

		this.startInteraction(event, element, "move");
	}

	startResize(
		event: PointerEvent,
		element: LayoutElement,
		handle: ResizeHandle,
	): void {
		if (event.button !== 0) {
			return;
		}

		this.startInteraction(event, element, "resize", handle);
	}

	onPointerMove(event: PointerEvent): void {
		const interaction = this.interaction;

		if (!interaction || interaction.pointerId !== event.pointerId) {
			return;
		}

		event.preventDefault();

		const deltaX =
			((event.clientX - interaction.startClientX) /
				interaction.canvasWidth) *
			100;

		const deltaY =
			((event.clientY - interaction.startClientY) /
				interaction.canvasHeight) *
			100;

		const candidate =
			interaction.mode === "move"
				? this.calculateMove(
						interaction.originalElement,
						deltaX,
						deltaY,
					)
				: this.calculateResize(
						interaction.originalElement,
						interaction.handle as ResizeHandle,
						deltaX,
						deltaY,
					);

		const normalizedCandidate = this.roundElement(candidate);

		if (interaction.mode === "move") {
			if (
				this.isValidCandidate(
					normalizedCandidate,
					interaction.elementId,
				)
			) {
				this.replaceElement(interaction.elementId, normalizedCandidate);

				this.errorMessage = "";
			} else {
				this.errorMessage =
					"La zona no puede salir del área ni sobreponerse con otra.";
			}

			return;
		}

		const resolvedElements = this.resolveResizeWithNeighbors(
			normalizedCandidate,
			interaction,
		);

		if (!resolvedElements) {
			this.errorMessage =
				"No existe suficiente espacio para redimensionar los paneles.";

			return;
		}

		this.elements = resolvedElements;
		this.errorMessage = "";
	}

	private resolveResizeWithNeighbors(
		candidate: LayoutElement,
		interaction: InteractionState,
	): LayoutElement[] | null {
		if (!interaction.handle) {
			return null;
		}

		let adjustedCandidate = {
			...candidate,
		};

		const originalElements = interaction.originalElements.map(
			(element) => ({
				...element,
			}),
		);

		for (let attempt = 0; attempt < 20; attempt += 1) {
			const workingElements = originalElements.map((element) => ({
				...element,
			}));

			let candidateWasAdjusted = false;

			for (let index = 0; index < workingElements.length; index += 1) {
				const neighbor = workingElements[index];

				if (neighbor.id === interaction.elementId) {
					continue;
				}

				if (!this.elementsOverlap(adjustedCandidate, neighbor)) {
					continue;
				}

				const collisionDirection = this.getCollisionDirection(
					adjustedCandidate,
					interaction.originalElement,
					neighbor,
				);

				if (!collisionDirection) {
					return null;
				}

				if (neighbor.fixedSize) {
					if (adjustedCandidate.lockAspectRatio) {
						return null;
					}

					const clampedCandidate = this.clampCandidateAgainstObstacle(
						adjustedCandidate,
						neighbor,
						collisionDirection,
					);

					if (
						this.hasSameGeometry(
							adjustedCandidate,
							clampedCandidate,
						)
					) {
						return null;
					}

					adjustedCandidate = this.roundElement(clampedCandidate);

					if (!this.isInsideCanvas(adjustedCandidate)) {
						return null;
					}

					candidateWasAdjusted = true;
					break;
				}

				const resizedNeighbor = this.resizeNeighborAgainstCandidate(
					neighbor,
					adjustedCandidate,
					collisionDirection,
				);

				if (!resizedNeighbor) {
					if (adjustedCandidate.lockAspectRatio) {
						return null;
					}

					const clampedCandidate = this.clampCandidateAgainstObstacle(
						adjustedCandidate,
						neighbor,
						collisionDirection,
					);

					if (
						this.hasSameGeometry(
							adjustedCandidate,
							clampedCandidate,
						)
					) {
						return null;
					}

					adjustedCandidate = this.roundElement(clampedCandidate);

					if (!this.isInsideCanvas(adjustedCandidate)) {
						return null;
					}

					candidateWasAdjusted = true;
					break;
				}

				workingElements[index] = this.roundElement(resizedNeighbor);
			}

			if (candidateWasAdjusted) {
				continue;
			}

			return workingElements.map((element) =>
				element.id === interaction.elementId
					? adjustedCandidate
					: element,
			);
		}

		return null;
	}

	private getCollisionDirection(
		candidate: LayoutElement,
		originalElement: LayoutElement,
		neighbor: LayoutElement,
	): CollisionDirection | null {
		const tolerance = 0.001;

		const originalRight = originalElement.x + originalElement.width;

		const originalBottom = originalElement.y + originalElement.height;

		const candidateRight = candidate.x + candidate.width;

		const candidateBottom = candidate.y + candidate.height;

		const neighborRight = neighbor.x + neighbor.width;

		const neighborBottom = neighbor.y + neighbor.height;

		const overlapX =
			Math.min(candidateRight, neighborRight) -
			Math.max(candidate.x, neighbor.x);

		const overlapY =
			Math.min(candidateBottom, neighborBottom) -
			Math.max(candidate.y, neighbor.y);

		const expandsEast = candidateRight > originalRight + tolerance;

		const expandsWest = candidate.x < originalElement.x - tolerance;

		const expandsSouth = candidateBottom > originalBottom + tolerance;

		const expandsNorth = candidate.y < originalElement.y - tolerance;

		const directions: Array<{
			direction: CollisionDirection;
			penetration: number;
		}> = [];

		if (expandsEast && neighbor.x >= originalRight - tolerance) {
			directions.push({
				direction: "e",
				penetration: overlapX,
			});
		}

		if (expandsWest && neighborRight <= originalElement.x + tolerance) {
			directions.push({
				direction: "w",
				penetration: overlapX,
			});
		}

		if (expandsSouth && neighbor.y >= originalBottom - tolerance) {
			directions.push({
				direction: "s",
				penetration: overlapY,
			});
		}

		if (expandsNorth && neighborBottom <= originalElement.y + tolerance) {
			directions.push({
				direction: "n",
				penetration: overlapY,
			});
		}

		directions.sort(
			(first, second) => first.penetration - second.penetration,
		);

		return directions.length > 0 ? directions[0].direction : null;
	}

	private resizeNeighborAgainstCandidate(
		neighbor: LayoutElement,
		candidate: LayoutElement,
		direction: CollisionDirection,
	): LayoutElement | null {
		const resizedNeighbor = {
			...neighbor,
		};

		switch (direction) {
			case "e": {
				const neighborRight = neighbor.x + neighbor.width;

				const newX = candidate.x + candidate.width;

				const newWidth = neighborRight - newX;

				if (newWidth < this.minimumSize) {
					return null;
				}

				resizedNeighbor.x = newX;
				resizedNeighbor.width = newWidth;

				break;
			}

			case "w": {
				const newWidth = candidate.x - neighbor.x;

				if (newWidth < this.minimumSize) {
					return null;
				}

				resizedNeighbor.width = newWidth;

				break;
			}

			case "s": {
				const neighborBottom = neighbor.y + neighbor.height;

				const newY = candidate.y + candidate.height;

				const newHeight = neighborBottom - newY;

				if (newHeight < this.minimumSize) {
					return null;
				}

				resizedNeighbor.y = newY;
				resizedNeighbor.height = newHeight;

				break;
			}

			case "n": {
				const newHeight = candidate.y - neighbor.y;

				if (newHeight < this.minimumSize) {
					return null;
				}

				resizedNeighbor.height = newHeight;

				break;
			}
		}

		const proportionalNeighbor = this.applyNeighborAspectRatio(
			neighbor,
			resizedNeighbor,
			direction,
		);

		if (!proportionalNeighbor) {
			return null;
		}

		return this.isInsideCanvas(proportionalNeighbor)
			? proportionalNeighbor
			: null;

		return this.isInsideCanvas(resizedNeighbor) ? resizedNeighbor : null;
	}

	private applyNeighborAspectRatio(
		originalNeighbor: LayoutElement,
		resizedNeighbor: LayoutElement,
		direction: CollisionDirection,
	): LayoutElement | null {
		if (!originalNeighbor.lockAspectRatio) {
			return resizedNeighbor;
		}

		const ratio = this.getElementAspectRatio(originalNeighbor);

		const adjustedNeighbor = {
			...resizedNeighbor,
		};

		if (direction === "e" || direction === "w") {
			const newHeight = this.getHeightFromWidth(
				resizedNeighbor.width,
				ratio,
			);

			if (newHeight < this.minimumSize) {
				return null;
			}

			adjustedNeighbor.height = newHeight;

			adjustedNeighbor.y =
				originalNeighbor.y + (originalNeighbor.height - newHeight) / 2;
		} else {
			const newWidth = this.getWidthFromHeight(
				resizedNeighbor.height,
				ratio,
			);

			if (newWidth < this.minimumSize) {
				return null;
			}

			adjustedNeighbor.width = newWidth;

			adjustedNeighbor.x =
				originalNeighbor.x + (originalNeighbor.width - newWidth) / 2;
		}

		return this.roundElement(adjustedNeighbor);
	}

	private clampCandidateAgainstObstacle(
		candidate: LayoutElement,
		obstacle: LayoutElement,
		direction: CollisionDirection,
	): LayoutElement {
		const clampedCandidate = {
			...candidate,
		};

		switch (direction) {
			case "e": {
				clampedCandidate.width = obstacle.x - clampedCandidate.x;

				break;
			}

			case "w": {
				const originalRight =
					clampedCandidate.x + clampedCandidate.width;

				clampedCandidate.x = obstacle.x + obstacle.width;

				clampedCandidate.width = originalRight - clampedCandidate.x;

				break;
			}

			case "s": {
				clampedCandidate.height = obstacle.y - clampedCandidate.y;

				break;
			}

			case "n": {
				const originalBottom =
					clampedCandidate.y + clampedCandidate.height;

				clampedCandidate.y = obstacle.y + obstacle.height;

				clampedCandidate.height = originalBottom - clampedCandidate.y;

				break;
			}
		}

		return clampedCandidate;
	}

	onPointerEnd(event: PointerEvent): void {
		const interaction = this.interaction;

		if (!interaction || interaction.pointerId !== event.pointerId) {
			return;
		}

		const canvas = this.canvasRef.nativeElement;

		if (
			canvas.hasPointerCapture &&
			canvas.hasPointerCapture(event.pointerId)
		) {
			canvas.releasePointerCapture(event.pointerId);
		}

		this.interaction = null;
		this.emitValue();
	}

	updateNumericField(
		field: NumericField,
		rawValue: string,
		input: HTMLInputElement,
	): void {
		const selected = this.selectedElement;
		const value = Number(rawValue);

		if (!selected || !Number.isFinite(value)) {
			return;
		}

		const candidate = this.roundElement({
			...selected,
			[field]: value,
		});

		if (field === "width" || field === "height") {
			const handle: ResizeHandle = field === "width" ? "e" : "s";

			const virtualInteraction: InteractionState = {
				mode: "resize",
				pointerId: -1,
				elementId: selected.id,
				handle,
				startClientX: 0,
				startClientY: 0,
				canvasWidth: 1,
				canvasHeight: 1,

				originalElement: {
					...selected,
				},

				originalElements: this.elements.map((element) => ({
					...element,
				})),
			};

			const resolvedElements = this.resolveResizeWithNeighbors(
				candidate,
				virtualInteraction,
			);

			if (!resolvedElements) {
				input.value = String(selected[field]);

				this.errorMessage =
					"No existe suficiente espacio para aplicar ese tamaño.";

				return;
			}

			this.elements = resolvedElements;
			this.errorMessage = "";
			this.emitValue();

			return;
		}

		if (!this.isValidCandidate(candidate, selected.id)) {
			input.value = String(selected[field]);

			this.errorMessage =
				"El valor ingresado provoca una posición inválida o una colisión.";

			return;
		}

		this.replaceElement(selected.id, candidate);

		this.errorMessage = "";
		this.emitValue();
	}

	updateName(name: string): void {
		const selected = this.selectedElement;

		if (!selected) {
			return;
		}

		const normalizedName = name.trim() || selected.name;

		this.replaceElement(selected.id, {
			...selected,
			name: normalizedName,
		});

		this.emitValue();
	}

	trackByElementId(index: number, element: LayoutElement): string {
		return element.id;
	}

	trackByHandle(index: number, handle: ResizeHandle): ResizeHandle {
		return handle;
	}

	private startInteraction(
		event: PointerEvent,
		element: LayoutElement,
		mode: "move" | "resize",
		handle?: ResizeHandle,
	): void {
		event.preventDefault();
		event.stopPropagation();

		const canvas = this.canvasRef.nativeElement;
		const canvasRect = canvas.getBoundingClientRect();

		if (canvas.setPointerCapture) {
			canvas.setPointerCapture(event.pointerId);
		}

		this.selectedId = element.id;
		this.errorMessage = "";

		this.interaction = {
			mode,
			handle,
			pointerId: event.pointerId,
			elementId: element.id,
			startClientX: event.clientX,
			startClientY: event.clientY,
			canvasWidth: canvasRect.width,
			canvasHeight: canvasRect.height,

			originalElement: {
				...element,
			},

			originalElements: this.elements.map((currentElement) => ({
				...currentElement,
			})),
		};
	}

	private calculateMove(
		element: LayoutElement,
		deltaX: number,
		deltaY: number,
	): LayoutElement {
		return {
			...element,
			x: this.clamp(element.x + deltaX, 0, 100 - element.width),
			y: this.clamp(element.y + deltaY, 0, 100 - element.height),
		};
	}

	private calculateResize(
		element: LayoutElement,
		handle: ResizeHandle,
		deltaX: number,
		deltaY: number,
	): LayoutElement {
		if (element.lockAspectRatio) {
			return this.calculateLockedResize(element, handle, deltaX, deltaY);
		}

		return this.calculateFreeResize(element, handle, deltaX, deltaY);
	}

	private calculateFreeResize(
		element: LayoutElement,
		handle: ResizeHandle,
		deltaX: number,
		deltaY: number,
	): LayoutElement {
		let left = element.x;
		let top = element.y;
		let right = element.x + element.width;
		let bottom = element.y + element.height;

		if (handle.indexOf("e") !== -1) {
			right = this.clamp(right + deltaX, left + this.minimumSize, 100);
		}

		if (handle.indexOf("w") !== -1) {
			left = this.clamp(left + deltaX, 0, right - this.minimumSize);
		}

		if (handle.indexOf("s") !== -1) {
			bottom = this.clamp(bottom + deltaY, top + this.minimumSize, 100);
		}

		if (handle.indexOf("n") !== -1) {
			top = this.clamp(top + deltaY, 0, bottom - this.minimumSize);
		}

		return {
			...element,
			x: left,
			y: top,
			width: right - left,
			height: bottom - top,
		};
	}

	private calculateLockedResize(
		element: LayoutElement,
		handle: ResizeHandle,
		deltaX: number,
		deltaY: number,
	): LayoutElement {
		const ratio = this.getElementAspectRatio(element);

		const originalRight = element.x + element.width;

		const originalBottom = element.y + element.height;

		const centerX = element.x + element.width / 2;

		const centerY = element.y + element.height / 2;

		const changesHorizontal =
			handle.indexOf("e") !== -1 || handle.indexOf("w") !== -1;

		const changesVertical =
			handle.indexOf("n") !== -1 || handle.indexOf("s") !== -1;

		let desiredWidth = element.width;
		let desiredHeight = element.height;

		if (handle.indexOf("e") !== -1) {
			desiredWidth = element.width + deltaX;
		}

		if (handle.indexOf("w") !== -1) {
			desiredWidth = element.width - deltaX;
		}

		if (handle.indexOf("s") !== -1) {
			desiredHeight = element.height + deltaY;
		}

		if (handle.indexOf("n") !== -1) {
			desiredHeight = element.height - deltaY;
		}

		let requestedWidth: number;

		if (changesHorizontal && changesVertical) {
			const horizontalScale = desiredWidth / element.width;

			const verticalScale = desiredHeight / element.height;

			const horizontalDifference = Math.abs(horizontalScale - 1);

			const verticalDifference = Math.abs(verticalScale - 1);

			requestedWidth =
				horizontalDifference >= verticalDifference
					? desiredWidth
					: this.getWidthFromHeight(desiredHeight, ratio);
		} else if (changesHorizontal) {
			requestedWidth = desiredWidth;
		} else {
			requestedWidth = this.getWidthFromHeight(desiredHeight, ratio);
		}

		const minimumWidth = Math.max(
			this.minimumSize,
			this.getWidthFromHeight(this.minimumSize, ratio),
		);

		let maximumHorizontalWidth: number;

		if (handle.indexOf("e") !== -1) {
			maximumHorizontalWidth = 100 - element.x;
		} else if (handle.indexOf("w") !== -1) {
			maximumHorizontalWidth = originalRight;
		} else {
			maximumHorizontalWidth = 2 * Math.min(centerX, 100 - centerX);
		}

		let maximumVerticalHeight: number;

		if (handle.indexOf("s") !== -1) {
			maximumVerticalHeight = 100 - element.y;
		} else if (handle.indexOf("n") !== -1) {
			maximumVerticalHeight = originalBottom;
		} else {
			maximumVerticalHeight = 2 * Math.min(centerY, 100 - centerY);
		}

		const maximumVerticalWidth = this.getWidthFromHeight(
			maximumVerticalHeight,
			ratio,
		);

		const maximumWidth = Math.min(
			maximumHorizontalWidth,
			maximumVerticalWidth,
		);

		if (maximumWidth < minimumWidth) {
			return {
				...element,
			};
		}

		const newWidth = this.clamp(requestedWidth, minimumWidth, maximumWidth);

		const newHeight = this.getHeightFromWidth(newWidth, ratio);

		let newX: number;
		let newY: number;

		if (handle.indexOf("w") !== -1) {
			newX = originalRight - newWidth;
		} else if (handle.indexOf("e") !== -1) {
			newX = element.x;
		} else {
			newX = centerX - newWidth / 2;
		}

		if (handle.indexOf("n") !== -1) {
			newY = originalBottom - newHeight;
		} else if (handle.indexOf("s") !== -1) {
			newY = element.y;
		} else {
			newY = centerY - newHeight / 2;
		}

		return {
			...element,
			x: newX,
			y: newY,
			width: newWidth,
			height: newHeight,
		};
	}

	private getElementAspectRatio(element: LayoutElement): number {
		if (
			typeof element.aspectRatio === "number" &&
			element.aspectRatio > 0
		) {
			return element.aspectRatio;
		}

		return this.calculateVisualAspectRatio(element);
	}

	private getHeightFromWidth(width: number, ratio: number): number {
		return (width * this.canvasAspectRatio) / ratio;
	}

	private getWidthFromHeight(height: number, ratio: number): number {
		return (height * ratio) / this.canvasAspectRatio;
	}

	private isValidCandidate(
		candidate: LayoutElement,
		ignoredElementId: string,
	): boolean {
		if (
			candidate.width < this.minimumSize ||
			candidate.height < this.minimumSize
		) {
			return false;
		}

		if (candidate.x < 0 || candidate.y < 0) {
			return false;
		}

		if (
			candidate.x + candidate.width > 100 ||
			candidate.y + candidate.height > 100
		) {
			return false;
		}

		return !this.elements
			.filter((element) => element.id !== ignoredElementId)
			.some((element) => this.elementsOverlap(candidate, element));
	}

	private elementsOverlap(
		first: LayoutElement,
		second: LayoutElement,
	): boolean {
		const tolerance = 0.0001;

		return (
			first.x < second.x + second.width - tolerance &&
			first.x + first.width > second.x + tolerance &&
			first.y < second.y + second.height - tolerance &&
			first.y + first.height > second.y + tolerance
		);
	}

	updateFixedSize(checked: boolean): void {
		const selected = this.selectedElement;

		if (!selected) {
			return;
		}

		this.replaceElement(selected.id, {
			...selected,
			fixedSize: checked,
		});

		this.errorMessage = "";
		this.emitValue();
	}

	private findAvailablePosition(
		width: number,
		height: number,
	): { x: number; y: number } | null {
		for (let y = 0; y <= 100 - height; y += 1) {
			for (let x = 0; x <= 100 - width; x += 1) {
				const candidate: LayoutElement = {
					id: "__candidate__",
					name: "",
					type: "VIDEO",
					x,
					y,
					width,
					height,
					fixedSize: false,
					lockAspectRatio: false,
				};

				const collision = this.elements.some((element) =>
					this.elementsOverlap(candidate, element),
				);

				if (!collision) {
					return { x, y };
				}
			}
		}

		return null;
	}

	saveLayout(): void {
		const normalizedLayoutName = this.layoutName.trim();

		if (!normalizedLayoutName) {
			this.errorMessage = "Debe ingresar el nombre del layout.";

			return;
		}

		if (this.elements.length === 0) {
			this.errorMessage =
				"Debe agregar al menos una zona antes de guardar.";

			return;
		}

		const layoutJson = {
			name: normalizedLayoutName,
			aspectRatio: "16:9",

			elements: this.elements.map((element) => ({
				id: element.id,
				name: element.name,
				type: element.type,
				x: element.x,
				y: element.y,
				width: element.width,
				height: element.height,
				fixedSize: element.fixedSize,
				lockAspectRatio: element.lockAspectRatio,
				aspectRatio: element.aspectRatio,
			})),
		};

		this.errorMessage = "";

		console.log("Objeto del layout:", layoutJson);

		console.log("JSON del layout:", JSON.stringify(layoutJson, null, 2));
	}

	private replaceElement(
		elementId: string,
		updatedElement: LayoutElement,
	): void {
		this.elements = this.elements.map((element) =>
			element.id === elementId ? updatedElement : element,
		);
	}

	private emitValue(): void {
		this.valueChange.emit(
			this.elements.map((element) => ({
				...element,
			})),
		);
	}

	private roundElement(element: LayoutElement): LayoutElement {
		return {
			...element,
			x: this.round(element.x),
			y: this.round(element.y),
			width: this.round(element.width),
			height: this.round(element.height),
		};
	}

	private round(value: number): number {
		return Math.round(value * 1000) / 1000;
	}

	private clamp(value: number, minimum: number, maximum: number): number {
		return Math.min(Math.max(value, minimum), maximum);
	}

	private createId(): string {
		return (
			"zone-" + Date.now() + "-" + Math.random().toString(36).substr(2, 9)
		);
	}
}
